import { FC, useEffect, useMemo, useState } from 'react';
import {
  InlineLoading,
  InlineNotification,
  Modal,
  TextInput,
} from '@carbon/react';
import { DataTable } from 'components/data-table';
import type {
  DataTableColumnDef,
  DataTableSelectionState,
} from 'components/data-table/types';
import { FormattedCount } from 'components/formatted-count';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import type { WellQueryRow } from 'types/well-workspace';
import { useCreateWorkspace, useWells } from './hooks';
import { defaultWorkspaceName } from './utils';
import { StatusDot } from 'components/well-cards';
import styles from './create-workspace-modal.module.scss';

interface Props {
  open: boolean;
  kind: 'property' | 'dca';
  // The current wells-listing query (text + filters). The modal pages the whole
  // filtered set through the server, so every well the map is filtered to can be
  // reached, irrespective of the listing's own page.
  query: Record<string, unknown>;
  onClose: () => void;
  onCreated: (id: number) => void;
}

// The keys the listing pages itself with, dropped from the inherited query so the modal's
// own paging is what the server reads.
const PAGING_KEYS = ['page', 'pageSize', 'page_size', 'limit'];

interface CandidateRow extends Record<string, unknown> {
  id: string;
  name: string;
  uwi: string;
  status: string;
  currentOperator: string;
}

// Columns in the mock's order and with its headers. `status` renders as a dot plus its
// label.
const COLUMNS: DataTableColumnDef<CandidateRow>[] = [
  { accessorKey: 'name', header: 'Well Name' },
  { accessorKey: 'uwi', header: 'UWI' },
  {
    accessorKey: 'status',
    header: 'Well Status',
    cell: ({ getValue }) => (
      <span className={styles.statusCell}>
        <StatusDot status={getValue<string>()} />
      </span>
    ),
  },
  { accessorKey: 'currentOperator', header: 'Current Operator' },
];

const toTableRows = (wells: WellQueryRow[]): CandidateRow[] =>
  wells.map((well) => ({
    id: well.uwi,
    name: well.name ?? '—',
    uwi: well.uwi,
    status: well.status ?? '',
    currentOperator: well.currentOperator ?? '—',
  }));

export const CreateWorkspaceModal: FC<Props> = ({
  open,
  kind,
  query,
  onClose,
  onCreated,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');

  // The listing's filters without its paging; the base every page request builds on.
  const filters = useMemo(() => {
    const rest = { ...query };
    PAGING_KEYS.forEach((key) => {
      delete rest[key];
    });
    return rest;
  }, [query]);

  // Requested only while the modal is open, so closing it does not keep a read alive.
  // One page at a time through the server, like the wells listing itself: `page` and the
  // page size (as `limit`, which sizes the elastic query, and `pageSize` for the database
  // variant). The modal's search goes to the server too, in place of the listing's text.
  // The first, unsearched page also asks for the filtered UWIs, which is how "all start
  // selected" is seeded.
  const seedable = page === 1 && search === '';
  const candidatesQuery = useMemo(
    () => ({
      ...filters,
      ...(search !== '' && { text: search }),
      page,
      pageSize,
      limit: pageSize,
      ...(seedable && { include_filtered_uwis: true }),
    }),
    [filters, search, page, pageSize, seedable]
  );
  const candidatesResult = useWells(candidatesQuery, open);
  // Memoized so the effects below track the fetched rows, not a fresh `[]` identity on
  // every render.
  const candidates = useMemo(
    () => candidatesResult.data?.rows ?? [],
    [candidatesResult.data]
  );
  const totalCount = candidatesResult.data?.totalCount ?? 0;

  // A new filter set is a new pick: the selection and the paging start over, so
  // checkmarks never linger on wells that are no longer listed.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setSelected(new Set());
    setPage(1);
    setSearch('');
  }, [filtersKey]);

  // Every candidate starts selected, per the mock's "all start selected" copy. The first
  // page carries the whole filtered set's UWIs for that; the aggregation behind them has
  // a size ceiling, so when it comes back short of the total the set is too large to
  // seed and the pick starts empty instead, with the copy saying so.
  const seedUwis = seedable ? candidatesResult.data?.filteredUwis : undefined;
  const allStartSelected =
    seedUwis !== undefined && seedUwis.length === totalCount && totalCount > 0;
  useEffect(() => {
    if (!allStartSelected || !seedUwis) return;
    setSelected(new Set(seedUwis.map((well) => well.uwi)));
  }, [allStartSelected, seedUwis]);

  const selectedUwis = useMemo(() => Array.from(selected), [selected]);
  const rows = useMemo(() => toTableRows(candidates), [candidates]);

  const {
    saving,
    error: createError,
    create,
  } = useCreateWorkspace((workspace) => onCreated(workspace.id));

  // Only the first read blanks the body; later pages keep the previous rows on screen.
  const loading = candidatesResult.isPending;
  const error =
    createError ??
    (candidatesResult.isError ? 'Failed to load wells for this filter' : null);

  // The name starts as the one the backend would assign — "Well Workspace — 3 wells —
  // Sep 11, 6:58 PM" — and follows the selection's count until the reader edits it;
  // from then on it is theirs. The time is the moment the modal opened, held so the
  // default does not tick while they choose wells. Reset each time the modal opens.
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [nameBlurred, setNameBlurred] = useState(false);
  const [openedAt, setOpenedAt] = useState(() => new Date());
  useEffect(() => {
    if (open) {
      setOpenedAt(new Date());
      setNameEdited(false);
      setNameBlurred(false);
    }
  }, [open]);
  const suggestedName = defaultWorkspaceName(selectedUwis.length, openedAt);
  useEffect(() => {
    if (!nameEdited) setName(suggestedName);
  }, [nameEdited, suggestedName]);
  const nameTrimmed = name.trim();
  const nameInvalid = nameBlurred && nameTrimmed.length === 0;

  const handleCreate = () =>
    create({ wellUwis: selectedUwis, kind, name: nameTrimmed });

  // The table's selection is controlled from the Set, page by page: the header box reads
  // checked when every row on this page is in the Set, and toggling it or a row only
  // reconciles the rows on screen. Rebuilding the Set from one page would drop every
  // well picked on a page navigated away from.
  const selection = useMemo<DataTableSelectionState>(() => {
    const onPage = rows.filter((row) => selected.has(row.id));
    const allOnPage = rows.length > 0 && onPage.length === rows.length;

    return allOnPage
      ? { selectAll: true, toggledNodes: [] }
      : { selectAll: false, toggledNodes: onPage.map((row) => row.id) };
  }, [rows, selected]);

  const handleSelectionChange = (next: DataTableSelectionState) => {
    const toggled = new Set(next.toggledNodes);
    const selectedIn = (uwi: string) => {
      return next.selectAll ? !toggled.has(uwi) : toggled.has(uwi);
    };
    setSelected((prev) => {
      const resolved = new Set(prev);
      rows.forEach((row) => {
        if (selectedIn(row.id)) resolved.add(row.id);
        else resolved.delete(row.id);
      });
      return resolved;
    });
  };

  return (
    <Modal
      open={open}
      className={styles.modal}
      modalHeading={
        kind === 'dca' ? 'Create DCA workspace' : 'Create well workspace'
      }
      primaryButtonText={saving ? 'Creating…' : 'Create workspace'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={
        saving ||
        loading ||
        selectedUwis.length === 0 ||
        nameTrimmed.length === 0
      }
      size="lg"
      // The name field takes focus on open. Left to itself the modal focused the
      // table's page-size select instead, and the browser scrolled the body to it.
      selectorPrimaryFocus="#create-workspace-name"
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={handleCreate}
    >
      <TextInput
        id="create-workspace-name"
        className={styles.nameField}
        labelText="Workspace name"
        value={name}
        disabled={saving}
        invalid={nameInvalid}
        invalidText="Enter a workspace name."
        onChange={(event) => {
          setName(event.target.value);
          setNameEdited(true);
        }}
        onBlur={() => setNameBlurred(true)}
      />

      <h5 className={styles.sectionTitle}>Add wells</h5>
      <p className={styles.intro}>
        These <FormattedCount value={totalCount} /> wells come from your
        filtered dataset
        {allStartSelected
          ? ' and all start selected. Uncheck any you want to exclude, or adjust the filters on the wells list to change which wells appear here.'
          : '. Check the ones to include, or adjust the filters on the wells list to change which wells appear here.'}
      </p>
      <p className={styles.selectedCount}>
        <FormattedCount value={selectedUwis.length} /> selected.
      </p>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={error}
          className={styles.notice}
        />
      )}

      {loading ? (
        <InlineLoading description="Loading wells…" />
      ) : (
        <div className={styles.tableArea}>
          {/* One server page at a time, 100 by default, with the server's total
              driving the pager. Search and page changes refetch; the selection is
              untouched by either, so narrowing to find one well and unchecking it
              leaves every other well as it was. No sort: the wells query takes none. */}
          <DataTable<CandidateRow>
            data={rows}
            columns={COLUMNS}
            getRowId={(row) => row.id}
            initialTableState={{ page: 1, pageSize: DEFAULT_PAGE_SIZE }}
            scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
            controlledState={{ selection }}
            onSelectionChange={(state) =>
              handleSelectionChange(state.selection)
            }
            onPaginationChange={(state) => {
              setPage(state.page);
              setPageSize(state.pageSize);
            }}
            onGlobalSearchChange={(state) => {
              setSearch(state.globalFilter.trim());
              setPage(1);
            }}
            searchPlaceholder="Search by name, UWI, or operator"
            config={{
              selectionType: 'multi',
              sorting: false,
              globalSearch: true,
              columnFilters: false,
              columnVisibility: false,
              columnReordering: false,
              columnResizing: false,
              columnPinning: false,
              export: false,
            }}
          />
        </div>
      )}
    </Modal>
  );
};
